/**
 * Loop Prepare — generate session prep via the existing AI Services APIM
 * `/conversation` pipeline (same backend as legacy SESSION PREP), which already
 * has access to client sensing, notes, and plan context.
 */

export type SessionPrepMode = "session_prep" | "brief_summary"

export type SessionPrepSection = {
  id: string
  title: string
  body: string
}

export type SessionPrepSource = {
  id: string
  label: string
  icon: "note" | "mic" | "plan"
}

export type SessionPrepRecord = {
  mode: SessionPrepMode
  generatedAt: string
  sinceDate: string | null
  sections: SessionPrepSection[]
  sources: SessionPrepSource[]
}

export type SessionPrepContextFlags = {
  lastNoteDate: string | null
  hasPriorNote?: boolean
  hasPlan?: boolean
  hasClientData?: boolean
}

const PREP_TTL_MS = 12 * 60 * 60 * 1000
/** Bump when source/honesty semantics change so legacy 12h caches are ignored. */
const PREP_CACHE_VERSION = 2

function cacheKey(clientId: string, practitionerId: string): string {
  return `vira-session-prep:v${PREP_CACHE_VERSION}:${practitionerId}:${clientId}`
}

export function loadCachedSessionPrep(
  clientId: string,
  practitionerId: string
): SessionPrepRecord | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(clientId, practitionerId))
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as SessionPrepRecord
    if (!parsed?.generatedAt || !Array.isArray(parsed.sections)) {
      return null
    }
    const age = Date.now() - new Date(parsed.generatedAt).getTime()
    if (!Number.isFinite(age) || age < 0 || age > PREP_TTL_MS) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function saveCachedSessionPrep(
  clientId: string,
  practitionerId: string,
  record: SessionPrepRecord
): void {
  try {
    window.localStorage.setItem(
      cacheKey(clientId, practitionerId),
      JSON.stringify(record)
    )
  } catch {
    // Ignore quota / private mode failures — generation still succeeded.
  }
}

export function clearCachedSessionPrep(
  clientId: string,
  practitionerId: string
): void {
  try {
    window.localStorage.removeItem(cacheKey(clientId, practitionerId))
  } catch {
    // ignore
  }
}

function formatShortDate(isoOrDate: string | Date): string {
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate
  if (Number.isNaN(d.getTime())) {
    return "recently"
  }
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })
}

function daysAgo(n: number): Date {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d
}

/**
 * Source chips for what Prepare actually has — never invent notes or a plan.
 */
export function buildSessionPrepSources(
  args: SessionPrepContextFlags
): SessionPrepSource[] {
  const sources: SessionPrepSource[] = []
  if (args.hasClientData) {
    sources.push({
      id: "data",
      label: `client data through ${formatShortDate(new Date())}`,
      icon: "note",
    })
  }
  if (args.lastNoteDate) {
    sources.push({
      id: "note",
      label: `your ${formatShortDate(args.lastNoteDate)} note`,
      icon: "mic",
    })
  } else if (args.hasPriorNote) {
    sources.push({
      id: "note",
      label: "prior notes",
      icon: "mic",
    })
  }
  if (args.hasPlan) {
    sources.push({ id: "plan", label: "their plan", icon: "plan" })
  }
  return sources
}

function resolveSinceDate(lastNoteDate: string | null): string {
  if (lastNoteDate) {
    return formatShortDate(lastNoteDate)
  }
  return formatShortDate(daysAgo(7))
}

function sessionPrepPrompt(args: {
  clientFirstName: string
  sinceDate: string
  hasPriorNote: boolean
  hasPlan: boolean
  hasClientData: boolean
}): string {
  const available: string[] = []
  if (args.hasClientData) {
    available.push("client sensing / mood data")
  }
  if (args.hasPriorNote) {
    available.push(`progress notes (since ${args.sinceDate})`)
  }
  if (args.hasPlan) {
    available.push("an active between-session plan (nudges / assessments)")
  }
  const availableLine =
    available.length > 0
      ? `Available context: ${available.join("; ")}.`
      : "Available context: none confirmed on the client record yet."

  const planBody = args.hasPlan
    ? "Summarize between-session nudge/assessment adherence since the last session: what was scheduled, completion rates, and notable gaps. Plain text with • bullets."
    : "• No active between-session plan is on file for this client.\\n• Say briefly that plan adherence cannot be reviewed yet."
  const planSection = `{
      "id": "plan",
      "title": "How last session's plan went",
      "body": "${planBody}"
    }`

  const happenedGuidance = args.hasPriorNote
    ? `4-6 short bullet lines of the most clinically significant developments since ${args.sinceDate}. Lead with any risk alert.`
    : "2-4 short bullets from available sensing data only. If there is no note and little sensing, say that there is not enough recent session documentation yet."

  return `
You are preparing a clinician for today's therapy session with ${args.clientFirstName}.
${availableLine}
Analyze only the available data. Do not invent notes, plans, adherence, or clinical events.

Return ONLY valid JSON (no markdown fences) with this exact shape:
{
  "since_date": "${args.sinceDate}",
  "sections": [
    {
      "id": "happened",
      "title": "What's happened since ${args.sinceDate}",
      "body": "${happenedGuidance} Plain text, use • for bullets."
    },
    ${planSection},
    {
      "id": "cover",
      "title": "What to cover today",
      "body": "Concrete suggested agenda for today's session in 3-5 bullets grounded only in available data. If data is thin, suggest opening questions rather than fabricated history."
    },
    {
      "id": "ask",
      "title": "Ask Vira about ${args.clientFirstName}",
      "body": "3 short suggested follow-up questions the clinician could ask Vira or the client today."
    }
  ]
}

Rules:
- Ground every claim in available client data, notes, and plan. Do not invent facts.
- If a topic has no data, say so briefly rather than fabricating.
- Never claim a prior note or plan exists when it is not listed in Available context.
- Keep each body under 180 words.
`
}

function briefSummaryPrompt(args: { clientFirstName: string }): string {
  return `
Please give a brief case overview for ${args.clientFirstName}: presenting goals, symptom history, behavioral data trends, and the treatment plan so far (~300 words).
If notes or plan data are missing, say so rather than inventing them.

Return ONLY valid JSON (no markdown fences) with this exact shape:
{
  "since_date": null,
  "sections": [
    {
      "id": "brief",
      "title": "Brief case overview",
      "body": "The ~300 word overview as plain text paragraphs."
    }
  ]
}
`
}

function extractJsonObject(text: string): unknown {
  const trimmed = text.trim()
  try {
    return JSON.parse(trimmed)
  } catch {
    // fall through
  }
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i)
  if (fenced?.[1]) {
    return JSON.parse(fenced[1].trim())
  }
  const start = trimmed.indexOf("{")
  const end = trimmed.lastIndexOf("}")
  if (start >= 0 && end > start) {
    return JSON.parse(trimmed.slice(start, end + 1))
  }
  throw new Error("Model response was not JSON")
}

function parseSections(
  payload: unknown,
  fallbackTitle: string
): { sinceDate: string | null; sections: SessionPrepSection[] } {
  if (!payload || typeof payload !== "object") {
    throw new Error("Session prep generation returned an invalid response.")
  }
  const record = payload as Record<string, unknown>
  const sinceDate =
    typeof record.since_date === "string" ? record.since_date : null
  const rawSections = Array.isArray(record.sections) ? record.sections : []
  const sections: SessionPrepSection[] = []
  for (const item of rawSections) {
    if (!item || typeof item !== "object") {
      continue
    }
    const section = item as Record<string, unknown>
    const id =
      typeof section.id === "string" ? section.id : `section-${sections.length}`
    const title =
      typeof section.title === "string" && section.title.trim()
        ? section.title.trim()
        : fallbackTitle
    const body =
      typeof section.body === "string" && section.body.trim()
        ? section.body.trim()
        : ""
    if (!body) {
      continue
    }
    sections.push({ id, title, body })
  }
  return { sinceDate, sections }
}

const CONVERSATION_MAX_ATTEMPTS = 3
const CONVERSATION_RETRY_BASE_MS = 1200

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function isRetryableConversationStatus(status: number): boolean {
  return status === 400 || status === 408 || status === 429 || status >= 500
}

function conversationErrorMessage(
  status: number,
  responseBody: string
): string {
  try {
    const parsed = JSON.parse(responseBody) as {
      error?: { message?: unknown }
      message?: unknown
    }
    const detail =
      (typeof parsed?.error?.message === "string" && parsed.error.message) ||
      (typeof parsed?.message === "string" && parsed.message) ||
      ""
    if (
      /connection slots|Failed to connect to PostgreSQL|get_client_data_postgres/i.test(
        detail
      )
    ) {
      return "Session prep is temporarily unavailable (AI data service is busy). Retry in a moment."
    }
    if (detail.trim()) {
      return `Session prep generation failed (${status}): ${detail
        .trim()
        .slice(0, 180)}`
    }
  } catch {
    // ignore non-JSON bodies
  }
  return `Session prep generation failed (${status}). Try again in a moment.`
}

/**
 * One-shot Ask Vira question via the same APIM `/conversation` path as session prep.
 */
export async function askViraAboutClient(args: {
  clientId: string
  practitionerId: string
  supervisorId?: string | null
  clientFirstName: string
  question: string
}): Promise<string> {
  const question = args.question.trim()
  if (!question) {
    throw new Error("Enter a question to ask Vira.")
  }
  const chatInput = `
You are assisting a clinician preparing for a session with ${args.clientFirstName}.
Answer this question using available client data, notes, and plan context.
Keep the answer under 180 words. Prefer short bullets when listing findings.
If data is missing, say so briefly rather than inventing facts.

Question: ${question}
`
  return callConversation({
    clientId: args.clientId,
    practitionerId: args.practitionerId,
    supervisorId: args.supervisorId,
    chatInput,
  })
}

async function callConversation(args: {
  clientId: string
  practitionerId: string
  supervisorId?: string | null
  chatInput: string
}): Promise<string> {
  const endpoint = process.env.REACT_APP_KSANA_OPENAI_APIM_ENDPOINT
  const apiKey = process.env.REACT_APP_KSANA_OPENAI_APIM_KEY
  if (!endpoint || !apiKey) {
    throw new Error(
      "AI Services are not configured in this environment (missing APIM endpoint/key)."
    )
  }

  const requestBody = JSON.stringify({
    chat_history: [],
    chat_input: args.chatInput,
    user_id: args.clientId,
    practitioner_id: args.practitionerId,
    supervisor_id: args.supervisorId ?? null,
  })

  let lastError: Error | null = null
  for (let attempt = 1; attempt <= CONVERSATION_MAX_ATTEMPTS; attempt++) {
    let response: Response
    try {
      response = await fetch(`${endpoint}/conversation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ksana-openai-key": apiKey,
        },
        body: requestBody,
      })
    } catch (networkError) {
      lastError =
        networkError instanceof Error
          ? networkError
          : new Error("Network error calling session prep.")
      if (attempt < CONVERSATION_MAX_ATTEMPTS) {
        await sleep(CONVERSATION_RETRY_BASE_MS * attempt)
        continue
      }
      throw lastError
    }

    if (!response.ok) {
      const responseBody = await response.text()
      lastError = new Error(
        conversationErrorMessage(response.status, responseBody)
      )
      if (
        attempt < CONVERSATION_MAX_ATTEMPTS &&
        isRetryableConversationStatus(response.status)
      ) {
        await sleep(CONVERSATION_RETRY_BASE_MS * attempt)
        continue
      }
      throw lastError
    }

    const body = (await response.json()) as { chat_output?: unknown }
    if (typeof body.chat_output !== "string" || !body.chat_output.trim()) {
      throw new Error("Session prep generation returned an empty response.")
    }
    return body.chat_output
  }

  throw lastError ?? new Error("Session prep generation failed.")
}

/**
 * Generate structured Loop session prep (or brief case summary) for a client.
 */
export async function generateSessionPrep(args: {
  mode: SessionPrepMode
  clientId: string
  clientFirstName: string
  practitionerId: string
  supervisorId?: string | null
  lastNoteDate: string | null
  hasPriorNote?: boolean
  hasPlan?: boolean
  hasClientData?: boolean
}): Promise<SessionPrepRecord> {
  const hasPriorNote = Boolean(args.hasPriorNote || args.lastNoteDate)
  const hasPlan = Boolean(args.hasPlan)
  const hasClientData = Boolean(args.hasClientData)
  const sinceDate = resolveSinceDate(args.lastNoteDate)
  const chatInput =
    args.mode === "brief_summary"
      ? briefSummaryPrompt({ clientFirstName: args.clientFirstName })
      : sessionPrepPrompt({
          clientFirstName: args.clientFirstName,
          sinceDate,
          hasPriorNote,
          hasPlan,
          hasClientData,
        })

  const chatOutput = await callConversation({
    clientId: args.clientId,
    practitionerId: args.practitionerId,
    supervisorId: args.supervisorId,
    chatInput,
  })

  let parsed: unknown
  try {
    parsed = extractJsonObject(chatOutput)
  } catch {
    throw new Error("Session prep generation returned an invalid response.")
  }

  const fallbackTitle =
    args.mode === "brief_summary"
      ? "Brief case overview"
      : `What's happened since ${sinceDate}`
  const { sinceDate: modelSince, sections } = parseSections(
    parsed,
    fallbackTitle
  )

  const record: SessionPrepRecord = {
    mode: args.mode,
    generatedAt: new Date().toISOString(),
    sinceDate: modelSince || sinceDate,
    sections,
    sources: buildSessionPrepSources({
      lastNoteDate: args.lastNoteDate,
      hasPriorNote,
      hasPlan,
      hasClientData,
    }),
  }
  if (sections.length > 0) {
    saveCachedSessionPrep(args.clientId, args.practitionerId, record)
  }
  return record
}
